import React, { useState } from 'react';
import { customersCol } from './database.js';
import { createDoc, updateOne } from './lib/db-crud.js';

// The three contact-method preference values stored on a customer.
// Keep this list in sync with the icons rendered in Home.jsx / Jobs.jsx
// (the `preferred` highlight on contact-action icons matches by value).
export const CONTACT_METHODS = [
  { value: 'phone', label: 'Phone (call)' },
  { value: 'text',  label: 'Text' },
  { value: 'email', label: 'Email' },
];

// Returns a fresh blank customer record. Used by the "Add Customer" flow.
export function blankCustomer() {
  return { name: '', phone: '', email: '', address: '', notes: '', preferredContact: [] };
}

// Modal for adding or editing one customer. Pass `initial` (a blank
// from blankCustomer() OR an existing customer) and `onClose`.
export default function CustomerModal({ initial, onClose }) {
  const isEditing = !!initial.id;
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  function update(field, value) {
    setForm((f) => ({ ...f, [field]: value }));
  }

  function togglePref(method) {
    setForm((f) => {
      const current = Array.isArray(f.preferredContact) ? f.preferredContact : [];
      const next = current.includes(method)
        ? current.filter((m) => m !== method)
        : [...current, method];
      return { ...f, preferredContact: next };
    });
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.name.trim()) { setError('Name is required.'); return; }
    setSubmitting(true);
    setError(null);
    try {
      // Trim everything; store empty strings as '' (not undefined) for consistency.
      const payload = {
        name: form.name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim(),
        address: form.address.trim(),
        notes: form.notes.trim(),
        preferredContact: Array.isArray(form.preferredContact) ? form.preferredContact : [],
      };
      if (isEditing) await updateOne(customersCol(), initial.id, payload);
      else await createDoc(customersCol(), payload);
      onClose();
    } catch (e) {
      setError(e.message);
      setSubmitting(false);
    }
  }

  const preferred = Array.isArray(form.preferredContact) ? form.preferredContact : [];

  return (
    <div className="modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal-box" role="dialog" aria-modal="true">
        <div className="modal-header">
          <h2>{isEditing ? 'Edit Customer' : 'Add Customer'}</h2>
          <button type="button" className="btn-link" onClick={onClose} aria-label="Close">&times;</button>
        </div>
        <form onSubmit={handleSubmit} className="form">
          <label className="form-row">
            <span>Name *</span>
            <input
              type="text" required maxLength={80} autoFocus
              value={form.name} onChange={(e) => update('name', e.target.value)}
              placeholder="First + last initial (e.g., Bob T.)"
            />
          </label>

          <label className="form-row">
            <span>Phone</span>
            <input
              type="tel" maxLength={20} inputMode="tel"
              value={form.phone} onChange={(e) => update('phone', e.target.value)}
              placeholder="(913) 555-1234"
            />
          </label>

          <label className="form-row">
            <span>Email</span>
            <input
              type="email" maxLength={120} inputMode="email"
              value={form.email} onChange={(e) => update('email', e.target.value)}
              placeholder="bob@example.com"
            />
          </label>

          <label className="form-row">
            <span>Address</span>
            <input
              type="text" maxLength={200}
              value={form.address} onChange={(e) => update('address', e.target.value)}
              placeholder="123 Maple St, Overland Park, KS"
            />
          </label>

          <div className="form-row">
            <span>Preferred contact</span>
            <div className="checkbox-group">
              {CONTACT_METHODS.map((m) => (
                <label key={m.value} className="checkbox-row">
                  <input
                    type="checkbox"
                    checked={preferred.includes(m.value)}
                    onChange={() => togglePref(m.value)}
                  />
                  {m.label}
                </label>
              ))}
            </div>
          </div>

          <label className="form-row">
            <span>Notes</span>
            <textarea
              maxLength={1000} rows={3}
              value={form.notes} onChange={(e) => update('notes', e.target.value)}
              placeholder="Garage code, gate access, scheduling preferences, anything to remember…"
            />
          </label>

          {error && <p className="form-error">{error}</p>}
          <div className="form-actions">
            <button type="button" className="btn-link" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn" disabled={submitting}>
              {submitting ? 'Saving…' : isEditing ? 'Save changes' : 'Add Customer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
